import { NavLink, Outlet, useMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, Newspaper, Download, FlaskConical, User, LayoutDashboard, Award, ShieldCheck, Info, ShoppingBag, Video } from "lucide-react";
import { DASHBOARD } from "@/constants/testIds";
import OnboardingTour from "@/components/OnboardingTour";
import { SidebarPreview } from "@/components/SidebarPreview";
import LiveWebinarBanner from "@/components/LiveWebinarBanner";
import DiscordCTA from "@/components/DiscordCTA";
import StreakFlame from "@/components/StreakFlame";

// Rendered as a separate component so we can call `useMatch` per row —
// keeps the className a plain string (not a function). The
// function-form `className` NavLink API doesn't survive Radix's Slot
// merge when a hover-card wraps the link, so we compute active state
// ourselves and always emit a static class string.
//
// `variant`:
//   - "sidebar" (default): vertical desktop sidebar row
//   - "pill"             : horizontal mobile pill in the scroll-strip
function SidebarLink({ item, t, variant = "sidebar" }) {
  const match = useMatch({ path: item.to, end: item.to === "/dashboard" });
  const isActive = !!match;

  const cls =
    variant === "pill"
      ? `shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
          isActive
            ? "bg-blue-800 text-white shadow-sm"
            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
        }`
      : `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? "bg-white text-blue-800 shadow-sm"
            : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
        }`;

  // The mobile pill row and the desktop aside would otherwise share the
  // same `data-testid`, and the pill row is first in DOM order. That means
  // `document.querySelector('[data-testid="dashboard-sidebar-courses"]')`
  // on desktop returns the *hidden* mobile pill (its wrapper is
  // `lg:hidden` = `display:none`), which breaks anything that depends on
  // finding the visible sidebar element — most notably the onboarding
  // tour, which then errors out on target-not-found and falls through to
  // `STATUS.FINISHED`. Suffix the mobile testId so each variant is
  // uniquely addressable.
  const resolvedTestId = item.testId
    ? variant === "pill" ? `${item.testId}-m` : item.testId
    : undefined;

  const link = (
    <NavLink
      to={item.to}
      end={item.to === "/dashboard"}
      data-testid={resolvedTestId}
      className={cls}
    >
      <item.icon size={variant === "pill" ? 14 : 16} />
      {item.label || t(item.labelKey)}
    </NavLink>
  );
  // The hover-card preview is a desktop-only nicety — mobile has no hover.
  if (variant === "pill") return link;
  return item.previewId ? <SidebarPreview id={item.previewId}>{link}</SidebarPreview> : link;
}

export default function DashboardShell() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const tier = user?.tier || "free";
  const isAdmin = user?.role === "admin";

  const NAV_ITEMS = [
    { to: "/dashboard",    labelKey: "dashboardShellUi.overview",     icon: LayoutDashboard, testId: DASHBOARD.sidebarOverview,     previewId: "overview" },
    { to: "/courses",      labelKey: "dashboardShellUi.courses",      icon: BookOpen,        testId: DASHBOARD.sidebarCourses,      previewId: "courses" },
    { to: "/articles",     labelKey: "dashboardShellUi.articles",     icon: Newspaper,       testId: DASHBOARD.sidebarArticles,     previewId: "articles" },
    { to: "/webinars",     labelKey: "dashboardShellUi.webinars",     icon: Video,           testId: "dashboard-sidebar-webinars",  previewId: "webinars" },
    { to: "/resources",    labelKey: "dashboardShellUi.resources",    icon: Download,        testId: DASHBOARD.sidebarResources,    previewId: "resources" },
    { to: "/ai-coach",     labelKey: "dashboardShellUi.aiCoach",      icon: FlaskConical,    testId: DASHBOARD.sidebarAiCoach,      previewId: "professor" },
    { to: "/achievements", labelKey: "dashboardShellUi.achievements", icon: Award,           testId: "dashboard-sidebar-achievements", previewId: "achievements" },
    { to: "/shop",         labelKey: "dashboardShellUi.shop",         icon: ShoppingBag,     testId: "dashboard-sidebar-shop",      previewId: "shop" },
    { to: "/account",      labelKey: "dashboardShellUi.account",      icon: User,            testId: DASHBOARD.sidebarAccount,      previewId: "account" },
    ...(isAdmin ? [{ to: "/admin", labelKey: "dashboardShellUi.admin", icon: ShieldCheck, testId: "dashboard-sidebar-admin", previewId: null }] : []),
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-10 lg:px-16 py-6 lg:py-10">
      <OnboardingTour />

      {/* Mobile: compact identity bar + horizontal scroll pill nav.
          The strip is sticky under the top navbar so the current page
          is always one tap away, and the scroll gives us room for all
          10+ items without eating vertical space. */}
      <div className="lg:hidden -mx-4 px-4 mb-4 sticky top-16 z-30 bg-white/95 backdrop-blur border-b border-slate-200/70 pb-2 pt-2">
        <div className="flex items-center justify-between mb-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-blue-800 font-semibold leading-none">
              {t("dashboardShellUi.memberArea")}
            </div>
            <div className="font-display font-bold text-slate-900 text-base leading-tight truncate">
              {user?.name || t("dashboardShellUi.studentFallback")}
            </div>
          </div>
          <span className="shrink-0 inline-flex items-center gap-2">
            <StreakFlame compact />
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-blue-50 text-blue-800">
              {t("dashboard.tierLabel", { tier: t(`tier.${tier}`) })}
            </span>
          </span>
        </div>
        <nav
          className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
          style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
        >
          {NAV_ITEMS.map((item) => (
            <SidebarLink key={item.to} item={item} t={t} variant="pill" />
          ))}
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-8">
        {/* Desktop-only vertical sidebar */}
        <aside className="hidden lg:block lg:sticky lg:top-24 h-max bg-slate-50 rounded-2xl shadow-sm p-5 -ml-5">
          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.2em] text-blue-800 mb-2">
              {t("dashboardShellUi.memberArea")}
            </div>
            <div className="font-display font-bold text-slate-900 text-xl leading-tight">
              {user?.name || t("dashboardShellUi.studentFallback")}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-blue-50 text-blue-800">
                {t("dashboard.tierLabel", { tier: t(`tier.${tier}`) })}
              </span>
              <StreakFlame />
            </div>
          </div>
          <nav className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <SidebarLink key={item.to} item={item} t={t} />
            ))}
          </nav>
          {/* Community CTA — Discord invite sits under the nav so members
              always have a one-tap way into the live rooms. */}
          <div className="mt-6 pt-4 border-t border-slate-200/70">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2 px-1">
              {t("dashboardShellUi.community")}
            </div>
            <DiscordCTA size="sm" label={t("dashboardShellUi.connectDiscord")} testId="dashboard-sidebar-discord" className="w-full justify-center" />
          </div>
        </aside>
        <main className="lg:pl-2 min-w-0">
          <LiveWebinarBanner />
          <Outlet />
          <p
            data-testid="affiliate-disclosure"
            className="mt-16 pt-6 border-t border-slate-200 text-[11px] leading-relaxed text-slate-400 flex items-start gap-2 max-w-3xl"
          >
            <Info size={12} className="mt-0.5 shrink-0" />
            <span>{t("dashboardShellUi.affiliateDisclosure")}</span>
          </p>
        </main>
      </div>
    </div>
  );
}
